import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import api from '../../api/client'
import { useAuth } from '../../hooks/useAuth'
import { parseStoredUser } from '../../utils/authSession'
import AppDropdown from '../../components/AppDropdown'
import AppCombobox from '../../components/AppCombobox'
import './LogoutModal.css'

async function fetchTaskOptionsPage(search, page, pageSize = 20) {
  const { data } = await api.get('/tasks/', {
    params: {
      page,
      page_size: pageSize,
      search: search || '',
      includeTotal: true,
      archive: false,
      status: 'IN_PROGRESS,DRAFT',
      fromDate: '1900-01-01',
      toDate: '9999-12-31',
    },
  })
  const raw = Array.isArray(data?.items) ? data.items : Array.isArray(data) ? data : []
  const items = raw.map((task) => ({
    value: String(task.id),
    label: task.task_name || task.task_code || `Task #${task.id}`,
  }))
  const total = Number(data?.total ?? 0)
  const hasMore = total > 0 ? page * pageSize < total : items.length === pageSize
  return { items, hasMore }
}

const FALLBACK_REASONS = [
  { code: 'END_OF_SHIFT', label: 'End of Shift', remarkRequired: false, allowCountInProductivity: false },
  { code: 'LUNCH_BREAK', label: 'Lunch Break', remarkRequired: false, allowCountInProductivity: false },
  { code: 'BREAK', label: 'Break', remarkRequired: false, allowCountInProductivity: false },
  { code: 'MEETING', label: 'Meeting', remarkRequired: true, allowCountInProductivity: true },
  { code: 'MANUAL', label: 'Other', remarkRequired: true, allowCountInProductivity: true },
]

function formatCountdown(seconds) {
  const safeSeconds = Number.isFinite(seconds) && seconds > 0 ? seconds : 0
  const mins = Math.floor(safeSeconds / 60)
  const secs = safeSeconds % 60
  return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`
}

function toClockFromHours(hours) {
  const totalSeconds = Math.max(0, Math.round(Number(hours || 0) * 3600))
  const h = Math.floor(totalSeconds / 3600)
  const m = Math.floor((totalSeconds % 3600) / 60)
  const s = totalSeconds % 60
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
}

export default function LogoutModal({
  isOpen,
  onClose,
  mode = 'manual',
  idleContext = null,
  systemContext = null,
  onSystemFailureAcknowledge = null,
}) {
  const navigate = useNavigate()
  const { logout } = useAuth()
  const isIdleWarningMode = mode === 'idle-warning'
  const isSystemFailureMode = mode === 'system-failure'

  const [reasons, setReasons] = useState(FALLBACK_REASONS)
  const [reason, setReason] = useState('END_OF_SHIFT')
  const [remark, setRemark] = useState('')
  const [countInProductivity, setCountInProductivity] = useState(false)
  const [manualHours, setManualHours] = useState('')
  const [manualMinutes, setManualMinutes] = useState('')
  const [taskId, setTaskId] = useState('')
  const [timerSeconds, setTimerSeconds] = useState(300)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState('')
  const [summary, setSummary] = useState(null)

  const selectedReason = useMemo(() => {
    return reasons.find((item) => item.code === reason) || null
  }, [reason, reasons])
  const reasonDropdownOptions = useMemo(
    () => reasons.map((item) => ({ value: item.code, label: item.label })),
    [reasons],
  )

  const remarkRequired = Boolean(selectedReason?.remarkRequired)
  const allowCountInProductivity = Boolean(selectedReason?.allowCountInProductivity)
  const parsedManualHours = Number.parseInt(String(manualHours || ''), 10)
  const parsedManualMinutes = Number.parseInt(String(manualMinutes || ''), 10)
  const hasValidManualHours = Number.isInteger(parsedManualHours) && parsedManualHours >= 0
  const hasValidManualMinutes =
    Number.isInteger(parsedManualMinutes) && parsedManualMinutes >= 0 && parsedManualMinutes <= 59
  const hasPositiveManualTime =
    hasValidManualHours && hasValidManualMinutes && ((parsedManualHours * 60) + parsedManualMinutes) > 0
  const needsManualTime = countInProductivity && allowCountInProductivity
  const canSubmit = isSystemFailureMode
    ? true
    : isIdleWarningMode
    ? true
    : Boolean(reason) &&
      (!remarkRequired || remark.trim()) &&
      (!needsManualTime || hasPositiveManualTime)

  const externalCountdown = Number(idleContext?.countdown)
  const effectiveTimerSeconds =
    isIdleWarningMode && Number.isFinite(externalCountdown) ? externalCountdown : timerSeconds
  const statusProductivity = idleContext?.sessionStatus?.productivity || {}

  useEffect(() => {
    if (!isOpen) return

    let active = true
    setError('')
    setSummary(null)
    setRemark('')
    setCountInProductivity(false)
    setManualHours('')
    setManualMinutes('')
    setTaskId('')

    if (isSystemFailureMode) {
      setReason('SYSTEM_FAILURE')
      return () => {
        active = false
      }
    }

    if (isIdleWarningMode) {
      setReason('USER_INACTIVE')
      return () => {
        active = false
      }
    }

    async function bootstrap() {
      const stored = parseStoredUser()
      if (stored?.role === 'employee' || stored?.is_employee) return
      try {
        const [reasonResponse, statusResponse] = await Promise.all([
          api.get('/activity-monitor/logout-reasons'),
          api.get('/activity-monitor/session-status'),
        ])

        if (!active) return

        const apiReasons = Array.isArray(reasonResponse?.data?.reasons)
          ? reasonResponse.data.reasons
          : FALLBACK_REASONS

        setReasons(apiReasons.length ? apiReasons : FALLBACK_REASONS)
        setReason(apiReasons[0]?.code || FALLBACK_REASONS[0].code)
        setCountInProductivity(false)

        const seconds = Number(statusResponse?.data?.logoutInSeconds)
        if (Number.isFinite(seconds) && seconds > 0) setTimerSeconds(seconds)
      } catch {
        if (!active) return
        setReasons(FALLBACK_REASONS)
        setReason(FALLBACK_REASONS[0].code)
      }
    }

    bootstrap()

    return () => {
      active = false
    }
  }, [isOpen, isIdleWarningMode, isSystemFailureMode])

  useEffect(() => {
    if (!allowCountInProductivity && countInProductivity) {
      setCountInProductivity(false)
      setManualHours('')
      setManualMinutes('')
    }
  }, [allowCountInProductivity, countInProductivity])

  useEffect(() => {
    if (!isOpen || isIdleWarningMode) return undefined

    const timer = window.setInterval(() => {
      setTimerSeconds((prev) => (prev > 0 ? prev - 1 : 0))
    }, 1000)

    return () => window.clearInterval(timer)
  }, [isOpen, isIdleWarningMode])

  async function handleConfirm() {
    if (!canSubmit) return

    if (isSystemFailureMode) {
      await onSystemFailureAcknowledge?.()
      return
    }

    setIsSubmitting(true)
    setError('')
    try {
      const payload = isIdleWarningMode
        ? {
            reason: 'USER_INACTIVE',
            remark: null,
            countInProductivity: false,
            manualHours: null,
            manualMinutes: null,
          }
        : {
            reason,
            remark: remark.trim() || null,
            countInProductivity: needsManualTime,
            manualHours: needsManualTime ? parsedManualHours : null,
            manualMinutes: needsManualTime ? parsedManualMinutes : null,
            taskId: reason === 'MEETING' && taskId ? Number(taskId) : null,
          }

      const response = await api.post('/activity-monitor/logout', payload)

      if (response?.data?.summary) {
        setSummary(response.data.summary)
        await new Promise((resolve) => window.setTimeout(resolve, 650))
      }

      try {
        const agentPort = sessionStorage.getItem('agentPort')
        if (agentPort) {
          await fetch(`http://localhost:${agentPort}/stop-tracking`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ reason: payload?.reason || null }),
          })
        }
      } catch {
        // Agent may be unreachable at logout.
      }

      sessionStorage.removeItem('agentPort')
      sessionStorage.removeItem('sessionId')
      const stored = parseStoredUser()
      const isEmp = stored?.role === 'employee' || stored?.is_employee
      logout()
      navigate(isEmp ? '/self-service/login' : '/login', { replace: true })
      if (onClose) onClose()
    } catch (requestError) {
      const message =
        requestError?.response?.data?.detail?.message ||
        requestError?.response?.data?.detail ||
        'Failed to logout. Please try again.'
      setError(typeof message === 'string' ? message : 'Failed to logout. Please try again.')
    } finally {
      setIsSubmitting(false)
    }
  }

  const stored = parseStoredUser()
  const isEmpModal = stored?.role === 'employee' || stored?.is_employee
  if (!isOpen || isEmpModal) return null

  const fallbackActiveHours = Number(statusProductivity?.activeHours || 0)
  const fallbackScore = Number(statusProductivity?.score || 0)
  const shiftClock = summary
    ? toClockFromHours(summary.activeHours || 0)
    : toClockFromHours(fallbackActiveHours)
  const progressWidth = summary
    ? `${Math.min(100, Math.round(Number(summary.productivityScore || 0)))}%`
    : `${Math.min(100, Math.round(fallbackScore || 0))}%`

  return (
    <div
      className="bridge-logout-overlay"
      onClick={isSystemFailureMode ? undefined : onClose}
    >
      <div className="bridge-logout-modal" onClick={(event) => event.stopPropagation()}>
        <div className="bridge-logout-main">
          <header className="bridge-logout-header">
            <p className="bridge-logout-kicker">System Action</p>
            <h2 className="bridge-logout-title">Logout Session</h2>
          </header>

          {summary ? (
            <section className="bridge-logout-summary">
              <p className="bridge-logout-summary-label">Today&apos;s Active Time</p>
              <h3 className="bridge-logout-summary-value">{Number(summary.activeHours || 0).toFixed(2)}h</h3>
              <p className="bridge-logout-summary-note">Productivity Score: {Number(summary.productivityScore || 0).toFixed(1)}%</p>
            </section>
          ) : isSystemFailureMode ? (
            <>
              <section className="bridge-logout-summary">
                <p className="bridge-logout-summary-label">Session Ended</p>
                <h3 className="bridge-logout-summary-value">System Failure</h3>
                <p className="bridge-logout-summary-note">
                  Agent heartbeat was lost and your session was closed for security.
                </p>
              </section>

              <section className="bridge-logout-fieldset">
                <label className="bridge-logout-label">What to do next</label>
                <p className="bridge-logout-summary-note">
                  1. Ensure desktop agent is running.
                  <br />
                  2. Check network connection briefly.
                  <br />
                  3. Login again to resume tracking.
                </p>
              </section>

              <footer className="bridge-logout-actions">
                <button type="button" onClick={handleConfirm} className="bridge-btn-primary">
                  Back to Login
                </button>
              </footer>
            </>
          ) : (
            <>
              <section className="bridge-logout-fieldset">
                <label className="bridge-logout-label" htmlFor="logoutReason">
                  Logout Reason Type
                </label>
                <AppDropdown
                  value={isIdleWarningMode ? 'USER_INACTIVE' : reason}
                  onChange={(value) => setReason(String(value))}
                  options={isIdleWarningMode ? [{ value: 'USER_INACTIVE', label: 'User Inactive' }] : reasonDropdownOptions}
                  disabled={isIdleWarningMode}
                  triggerClassName={`bridge-logout-input shadow-none ${isIdleWarningMode ? 'bridge-logout-readonly' : ''}`}
                  menuClassName="z-[220]"
                />
              </section>

              {reason === 'MEETING' && !isIdleWarningMode ? (
                <section className="bridge-logout-fieldset">
                  <label className="bridge-logout-label">
                    <span>Linked Task</span>
                    <span className="bridge-logout-required-note">Select task for meeting hours</span>
                  </label>
                  <AppCombobox
                    value={taskId}
                    onChange={(val) => setTaskId(val)}
                    placeholder="Select Task..."
                    searchPlaceholder="Search task..."
                    fetchOptions={fetchTaskOptionsPage}
                    triggerClassName="bridge-logout-input shadow-none w-full"
                  />
                </section>
              ) : null}

              <section className="bridge-logout-fieldset">
                <label className="bridge-logout-label" htmlFor="logoutRemark">
                  <span>
                    Remarks
                    {remarkRequired && !isIdleWarningMode ? <span style={{ color: '#ef4444', marginLeft: '4px' }}>*</span> : null}
                  </span>
                  <span className="bridge-logout-required-note">
                    {isIdleWarningMode ? 'System generated' : remarkRequired ? 'Required for selected reason' : 'Optional'}
                  </span>
                </label>
                <textarea
                  id="logoutRemark"
                  rows={3}
                  value={isIdleWarningMode ? 'Auto-logout due to inactivity.' : remark}
                  onChange={(event) => setRemark(event.target.value)}
                  disabled={isIdleWarningMode}
                  placeholder="Provide additional context for this logout event..."
                  className={`bridge-logout-textarea ${isIdleWarningMode ? 'bridge-logout-readonly' : ''}`}
                />
              </section>

              <section className="bridge-logout-toggle-row">
                <div className="bridge-logout-toggle-copy">
                  <span className="material-symbols-outlined">analytics</span>
                  <div>
                    <p>Count in Productivity</p>
                    <small>Session metrics will be calculated toward daily KPI.</small>
                  </div>
                </div>
                <button
                  type="button"
                  className={`bridge-switch ${countInProductivity && !isIdleWarningMode ? 'on' : 'off'}`}
                  onClick={() => {
                    if (!allowCountInProductivity || isIdleWarningMode) return
                    setCountInProductivity((prev) => !prev)
                  }}
                  aria-label="Toggle productivity count"
                  disabled={!allowCountInProductivity || isIdleWarningMode}
                >
                  <span />
                </button>
              </section>

              {!isIdleWarningMode && needsManualTime ? (
                <section className="bridge-logout-fieldset">
                  <label className="bridge-logout-label">
                    <span>
                      Counted Active Time <span style={{ color: '#ef4444', marginLeft: '4px' }}>*</span>
                    </span>
                    <span className="bridge-logout-required-note">Hours and minutes required</span>
                  </label>
                  <div className="bridge-logout-time-grid">
                    <input
                      type="number"
                      min={0}
                      step={1}
                      value={manualHours}
                      onChange={(event) => setManualHours(event.target.value)}
                      placeholder="Hours"
                      className="bridge-logout-input"
                    />
                    <input
                      type="number"
                      min={0}
                      max={59}
                      step={1}
                      value={manualMinutes}
                      onChange={(event) => setManualMinutes(event.target.value)}
                      placeholder="Minutes (0-59)"
                      className="bridge-logout-input"
                    />
                  </div>
                  {!hasPositiveManualTime ? (
                    <p className="bridge-logout-error">Enter valid hours and minutes greater than zero.</p>
                  ) : null}
                </section>
              ) : null}

              {error ? <p className="bridge-logout-error">{error}</p> : null}

              <footer className="bridge-logout-actions">
                <button type="button" onClick={handleConfirm} disabled={!canSubmit || isSubmitting} className="bridge-btn-primary">
                  {isSubmitting ? 'Logging out...' : 'Logout'}
                </button>
                <button type="button" onClick={onClose} disabled={isSubmitting} className="bridge-btn-secondary">
                  Cancel
                </button>
              </footer>
            </>
          )}
        </div>

        <aside className="bridge-logout-side">
          <section className="bridge-side-block">
            <div className="bridge-side-heading">
              <span className="material-symbols-outlined">shield</span>
              <span>Security Context</span>
            </div>
            <p>
              Logging out will terminate active bridge connections and sync your session cache to the enterprise hub.
            </p>
          </section>

          <section className="bridge-side-alert">
            <div className="bridge-side-alert-heading">
              <span className="material-symbols-outlined">timer</span>
              <span>Auto Logout Alert</span>
            </div>
            {isSystemFailureMode ? (
              <p>
                Reason: <strong>{String(systemContext?.reason || 'SYSTEM_FAILURE')}</strong>
              </p>
            ) : (
              <p>
                Your session expires in <strong>{formatCountdown(effectiveTimerSeconds)}</strong> due to inactivity.
              </p>
            )}
          </section>

          <section className="bridge-side-progress">
            <div className="bridge-side-progress-row">
              <span>Current Shift</span>
              <strong>{shiftClock}</strong>
            </div>
            <div className="bridge-side-progress-track">
              <span style={{ width: progressWidth }} />
            </div>
            <small>{isIdleWarningMode || !countInProductivity ? 'Excluded from KPI' : 'Included in KPI'} for this logout event</small>
          </section>
        </aside>
      </div>
    </div>
  )
}
